import React, { useEffect, useRef, useState } from 'react';
import { Box, Card, Stack, Typography } from '@mui/material';
import CusUpload from './CusUpload';
import { useTranslation } from 'react-i18next';
import { IconX } from '@tabler/icons-react';
import ShowContainer from './ShowContainer';
import { isValidFileName, isVideoFormat } from '../../utils';
import useToast from '../../hooks/useToast';
import { acceptImg } from '../../enums/enums';

export default function CusUploadFile({
  title,
  id,
  fileSource,
  onChange,
  flexDirection = 'column',
  accept = acceptImg,
  maxSizeMB,
  Component = Card
}) {
  const { errorToast } = useToast();
  const { t } = useTranslation();
  const [file, setFile] = useState(false);
  const [isVideo, setIsVideo] = useState(false);
  const [fileName, setFileName] = useState('');
  const inputRef = useRef();

  const onCrossClick = () => {
    setFile(null);
    setIsVideo(false);
    setFileName('');
    inputRef.current.value = '';
    onChange?.('');
  };

  useEffect(() => {
    setFile(fileSource);

    // Определяем тип файла по расширению из источника
    if (fileSource) {
      // Если это base64
      if (fileSource.startsWith('data:')) {
        const mimeType = fileSource.split(':')[1]?.split(';')[0];
        setIsVideo(mimeType?.startsWith('video/') || false);
      }
      // Если это URL или путь
      else if (typeof fileSource === 'string') {
        setIsVideo(isVideoFormat(fileSource));
      }
    }
  }, [fileSource]);

  const handleFileUpload = (file) => {
    if (!isValidFileName(file.name)) {
      errorToast(
        t(
          'The file name contains invalid characters. Only letters, numbers, dashes, underscores, and periods are allowed.'
        )
      );
      inputRef.current.value = '';
      return;
    }

    const reader = new FileReader();

    reader.onloadend = () => {
      const base64String = reader.result;
      setFile(base64String);
      setFileName(file.name);

      // Определяем тип файла
      const mimeType = file.type;
      setIsVideo(mimeType.startsWith('video/'));

      onChange?.(base64String, file.name);
    };

    reader.onerror = () => {
      errorToast(t('Error reading file. Please try again.'));
      inputRef.current.value = '';
    };

    reader.readAsDataURL(file);
  };

  const styleElem = { maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' };

  return (
    <Component sx={{ height: '100%' }}>
      <Stack
        display={'flex'}
        flexDirection={flexDirection}
        alignItems={flexDirection === 'row' ? 'center' : undefined}
        gap={1}
        height={'100%'}>
        <Typography variant="caption">{t(title)}</Typography>

        <ShowContainer condition={file}>
          <Box position="relative" padding={1} width="fit-content">
            {isVideo ? (
              <video style={styleElem} autoPlay muted loop playsInline>
                <source src={file} type="video/mp4" />
                {t('Your browser does not support the video tag.')}
              </video>
            ) : (
              <img style={styleElem} src={file} alt={fileName || t(title)} />
            )}

            <IconX
              style={{
                position: 'absolute',
                right: -15,
                top: -15,
                cursor: 'pointer',
                backgroundColor: 'white',
                borderRadius: '50%',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
              onClick={onCrossClick}
              size={20}
            />
          </Box>
        </ShowContainer>

        <CusUpload
          id={id}
          accept={accept}
          ref={inputRef}
          maxSizeMB={maxSizeMB}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileUpload(file);
          }}
        />
      </Stack>
    </Component>
  );
}
